/**
 * HighlightedTranscript
 * ──────────────────────
 * Renders transcript text, optionally highlighting char-offset spans that
 * matched a tagged Auto Sync reference document (see reference_match.py on
 * the backend). With no spans it renders identically to a plain
 * whitespace-pre-wrap paragraph — no visual change for sessions without a
 * tagged reference.
 */
import React from 'react';

export default function HighlightedTranscript({ text, spans, className }) {
  const safeText = text || '';
  const clean = (spans || [])
    .filter(s => Number.isFinite(s?.start) && Number.isFinite(s?.end) && s.end > s.start)
    .sort((a, b) => a.start - b.start);

  // Defensive clip against overlaps — keep the first (highest-scoring pass
  // already deduped upstream) and drop anything that starts before the
  // previous span ended.
  const merged = [];
  let cursor = 0;
  for (const s of clean) {
    if (s.start < cursor) continue;
    merged.push(s);
    cursor = s.end;
  }

  if (merged.length === 0) {
    return (
      <p className={className || 'text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap'} data-testid="highlighted-transcript">
        {safeText}
      </p>
    );
  }

  const nodes = [];
  let pos = 0;
  merged.forEach((s, i) => {
    if (s.start > pos) nodes.push(<React.Fragment key={`t-${i}`}>{safeText.slice(pos, s.start)}</React.Fragment>);
    nodes.push(
      <mark
        key={`m-${i}`}
        className="rounded px-0.5 bg-amber-200/60 text-slate-900"
        title={s.excerpt ? `Matches reference: “${s.excerpt}”` : 'Matches tagged reference material'}
        data-testid={`transcript-match-${i}`}>
        {safeText.slice(s.start, s.end)}
      </mark>
    );
    pos = s.end;
  });
  if (pos < safeText.length) nodes.push(<React.Fragment key="t-end">{safeText.slice(pos)}</React.Fragment>);

  return (
    <p className={className || 'text-[13px] text-slate-700 leading-relaxed whitespace-pre-wrap'} data-testid="highlighted-transcript">
      {nodes}
    </p>
  );
}
